<script setup>
import AiAgentBadge from '../AiAgentBadge.vue';
import HeaderAssigneePill from './HeaderAssigneePill.vue';
import HeaderFunnelPill from './HeaderFunnelPill.vue';
import TasksChip from 'dashboard/components-next/Conversation/ContextChips/TasksChip.vue';
import AgendaChip from 'dashboard/components-next/Conversation/ContextChips/AgendaChip.vue';
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHeaderPins } from 'dashboard/composables/useHeaderPins';

/**
 * Os módulos que a pessoa fixou no topo.
 *
 * Não existe componente novo aqui: é o MESMO módulo do trilho, renderizado na
 * forma expandida (a que morava no cabeçalho antes da V3). Fixar é escolher
 * qual das duas formas aparece e onde, não construir uma terceira.
 *
 * `pinnable` liga o "Voltar para a barra" dentro do popover do fixado: sem
 * ele, desafixar só dava pelo Personalizar cabeçalho (Lucas, 01/09).
 */
const props = defineProps({
  chat: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  itens: { type: Array, default: () => [] },
  // Com exceção na tela, o fixado encolhe para não competir com o aviso.
  compacto: { type: Boolean, default: false },
});

const { t } = useI18n();
const { reordenar } = useHeaderPins();

const abertoNo = ref(null);
const alternar = chave => {
  abertoNo.value = abertoNo.value === chave ? null : chave;
};
const fechar = () => {
  abertoNo.value = null;
};

// Reordenar por arrasto (mockup pin-mockup.html, estado 4): pressão longa
// levanta o item (cor de destaque), as vagas aparecem só enquanto ele está
// levantado, soltar numa vaga grava a ordem. Clique curto segue abrindo o
// popover do módulo. Shift + setas faz o mesmo pelo teclado.
const PRESSAO_LONGA_MS = 400;
const levantado = ref(null);
const vagaAlvo = ref(null);
const segurando = ref(null);
let timer = null;
let engoleProximoClique = false;

const arrastando = computed(() => levantado.value !== null);
// Vagas: uma antes de cada item e uma no fim, menos as duas coladas no próprio
// item levantado (soltar ali não muda nada).
const vagas = computed(() => {
  if (!arrastando.value) return [];
  const idx = props.itens.indexOf(levantado.value);
  return props.itens
    .map((_, i) => i)
    .concat(props.itens.length)
    .filter(i => i !== idx && i !== idx + 1);
});

const cancelarPressao = () => {
  clearTimeout(timer);
  timer = null;
  segurando.value = null;
};
const soltarTudo = () => {
  cancelarPressao();
  levantado.value = null;
  vagaAlvo.value = null;
};

const aoPressionar = (item, ev) => {
  if (ev.button !== undefined && ev.button !== 0) return;
  if (props.itens.length < 2) return;
  segurando.value = item;
  timer = setTimeout(() => {
    levantado.value = item;
    segurando.value = null;
  }, PRESSAO_LONGA_MS);
};

const aoMover = ev => {
  if (!arrastando.value) return;
  const el = document.elementFromPoint(ev.clientX, ev.clientY);
  const vaga = el?.closest?.('[data-vaga]');
  vagaAlvo.value = vaga ? Number(vaga.dataset.vaga) : null;
};

const moverPara = (item, vaga) => {
  const outros = props.itens.filter(m => m !== item);
  const idxAtual = props.itens.indexOf(item);
  const destino = vaga > idxAtual ? vaga - 1 : vaga;
  outros.splice(destino, 0, item);
  reordenar(outros);
};

const aoSoltar = () => {
  if (arrastando.value) {
    if (vagaAlvo.value !== null) moverPara(levantado.value, vagaAlvo.value);
    // o pointerup de um arrasto vira click no botão do módulo: engole esse um
    engoleProximoClique = true;
    setTimeout(() => {
      engoleProximoClique = false;
    }, 0);
  }
  soltarTudo();
};

const aoClicar = ev => {
  if (!engoleProximoClique) return;
  ev.stopPropagation();
  ev.preventDefault();
};

const aoTeclar = (item, ev) => {
  if (!ev.shiftKey || !['ArrowLeft', 'ArrowRight'].includes(ev.key)) return;
  const idx = props.itens.indexOf(item);
  const destino = ev.key === 'ArrowLeft' ? idx - 1 : idx + 1;
  if (destino < 0 || destino >= props.itens.length) return;
  ev.preventDefault();
  const nova = [...props.itens];
  nova.splice(idx, 1);
  nova.splice(destino, 0, item);
  reordenar(nova);
};

onBeforeUnmount(soltarTudo);
</script>

<template>
  <div
    v-if="itens.length"
    class="flex min-w-0 shrink-0 items-center gap-1.5"
    :class="{ 'select-none': arrastando }"
    data-testid="header-pinned-items"
    :data-arrastando="arrastando ? 'sim' : null"
    @pointermove="aoMover"
    @pointerup="aoSoltar"
    @pointercancel="soltarTudo"
    @pointerleave="arrastando && soltarTudo()"
  >
    <template v-for="(item, indice) in itens" :key="item">
      <span
        v-if="vagas.includes(indice)"
        class="inline-flex h-8 min-w-24 items-center justify-center rounded-full border-[1.5px] border-dashed border-n-blue-9 px-3 text-[11.5px] font-bold text-n-blue-11"
        :class="vagaAlvo === indice ? 'bg-n-blue-5' : 'bg-n-blue-2'"
        :data-vaga="indice"
        :data-testid="`pin-vaga-${indice}`"
      >
        {{ t('CONVERSATION.HEADER.PINS.DROP_HERE') }}
      </span>
      <span
        class="inline-flex rounded-full transition-[transform,box-shadow] duration-100"
        :class="{
          '-translate-y-0.5 scale-[1.03] cursor-grabbing shadow-lg shadow-n-blue-9/40 ring-2 ring-n-blue-9':
            levantado === item,
          'ring-2 ring-n-blue-7': segurando === item,
        }"
        :data-testid="`pinned-${item}`"
        :data-levantado="levantado === item ? 'sim' : null"
        :title="
          itens.length > 1
            ? t('CONVERSATION.HEADER.PINS.HOLD_TO_MOVE')
            : undefined
        "
        @pointerdown="aoPressionar(item, $event)"
        @keydown="aoTeclar(item, $event)"
        @click.capture="aoClicar"
      >
        <AiAgentBadge
          v-if="item === 'ai'"
          :compact="compacto"
          :open="abertoNo === 'ai'"
          :inbox-name="inbox?.name || ''"
          pinnable
          pinned
          @toggle="alternar('ai')"
          @close="fechar"
        />
        <HeaderAssigneePill
          v-else-if="item === 'agent'"
          :conversation-id="chat.id"
          :inbox-id="chat.inbox_id"
          :assignee="chat?.meta?.assignee"
          :compact="compacto"
          :open="abertoNo === 'agent'"
          pinnable
          pinned
          @toggle="alternar('agent')"
          @close="fechar"
        />
        <HeaderFunnelPill
          v-else-if="item === 'funnel'"
          :conversation="chat"
          :compact="compacto"
          :open="abertoNo === 'funnel'"
          pinnable
          pinned
          @toggle="alternar('funnel')"
          @close="fechar"
        />
        <TasksChip
          v-else-if="item === 'tasks'"
          :conversation="chat"
          :open="abertoNo === 'tasks'"
          :compact="compacto"
          pinnable
          pinned
          @toggle="alternar('tasks')"
          @close="fechar"
        />
        <AgendaChip
          v-else-if="item === 'agenda'"
          :conversation="chat"
          :open="abertoNo === 'agenda'"
          :compact="compacto"
          pinnable
          pinned
          @toggle="alternar('agenda')"
          @close="fechar"
        />
      </span>
    </template>
    <span
      v-if="vagas.includes(itens.length)"
      class="inline-flex h-8 min-w-24 items-center justify-center rounded-full border-[1.5px] border-dashed border-n-blue-9 px-3 text-[11.5px] font-bold text-n-blue-11"
      :class="vagaAlvo === itens.length ? 'bg-n-blue-5' : 'bg-n-blue-2'"
      :data-vaga="itens.length"
      :data-testid="`pin-vaga-${itens.length}`"
    >
      {{ t('CONVERSATION.HEADER.PINS.DROP_HERE') }}
    </span>
  </div>
</template>
